import axios from 'axios';
import { db, storage } from '../config/firebase';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from 'firebase/storage';
import {
  AssignGiftPayload,
  CreateGiftPayload,
  Gift,
  GiftAssignment,
  GiftAssignmentQRResponse,
  GiftResetConfig,
  GiftStoreAllocation,
  GiftUserSummary,
  ManualResetAllResponse,
  ManualResetGiftResponse,
  RedeemGiftPayload,
  RedeemGiftResponse,
  UpdateGiftPayload,
} from '../types/gift';

const API_URL = 'https://api-coffee.8am.vn/api';
const COLLECTION_NAME = 'gifts';
const ASSIGNMENTS_COLLECTION_NAME = 'giftAssignments';
const ASSIGNMENTS_HISTORY_COLLECTION_NAME = 'giftAssignmentsHistory';
const FIRESTORE_READ_TIMEOUT_MS = 8000;
const FIRESTORE_BATCH_LIMIT = 400;

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> => {
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutHandle = setTimeout(() => {
      reject(new Error(errorMessage));
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }
  }
};

const isFirebaseStorageUrl = (url: string): boolean => {
  try {
    return url.includes('firebasestorage.googleapis.com')
      || url.includes('storage.googleapis.com')
      || url.startsWith('gs://');
  } catch {
    return false;
  }
};

const normalizeStoreAllocation = (allocation: Partial<GiftStoreAllocation>): GiftStoreAllocation => {
  const totalQuantity = Number(allocation.totalQuantity ?? 0);
  const availableQuantity = Number(
    allocation.availableQuantity ?? totalQuantity,
  );
  const assignedCount = Number(allocation.assignedCount ?? Math.max(0, totalQuantity - availableQuantity));
  const usedQuantity = Number(allocation.usedQuantity ?? 0);

  return {
    storeId: String(allocation.storeId || ''),
    storeName: String(allocation.storeName || ''),
    totalQuantity,
    availableQuantity: Math.max(0, availableQuantity),
    assignedCount: Math.max(0, assignedCount),
    usedQuantity: Math.max(0, usedQuantity),
  };
};

const normalizeStoreAllocations = (storeAllocations?: Partial<GiftStoreAllocation>[]): GiftStoreAllocation[] => (
  Array.isArray(storeAllocations)
    ? storeAllocations
      .map(normalizeStoreAllocation)
      .filter((allocation) => allocation.storeId)
    : []
);

const normalizeResetConfig = (resetConfig?: Partial<GiftResetConfig> | null): GiftResetConfig | undefined => {
  if (!resetConfig) {
    return undefined;
  }

  const dailyResetHour = Number(resetConfig.dailyResetHour ?? 0);
  const dailyResetMinute = Number(resetConfig.dailyResetMinute ?? 0);
  const lastResetAtValue = (resetConfig as any).lastResetAt?.toDate?.()?.toISOString?.()
    || resetConfig.lastResetAt;

  return {
    enabled: Boolean(resetConfig.enabled),
    dailyResetHour: Math.min(23, Math.max(0, Number.isFinite(dailyResetHour) ? dailyResetHour : 0)),
    dailyResetMinute: Math.min(59, Math.max(0, Number.isFinite(dailyResetMinute) ? dailyResetMinute : 0)),
    ...(typeof lastResetAtValue === 'string' ? { lastResetAt: lastResetAtValue } : {}),
  };
};

const sumGiftTotals = (storeAllocations: GiftStoreAllocation[]) => ({
  totalQuantity: storeAllocations.reduce((sum, allocation) => sum + allocation.totalQuantity, 0),
  availableQuantity: storeAllocations.reduce((sum, allocation) => sum + allocation.availableQuantity, 0),
  assignedCount: storeAllocations.reduce((sum, allocation) => sum + allocation.assignedCount, 0),
  usedQuantity: storeAllocations.reduce((sum, allocation) => sum + allocation.usedQuantity, 0),
});

const normalizeGift = (gift: Partial<Gift> & { id: string }): Gift => {
  const storeAllocations = normalizeStoreAllocations(gift.storeAllocations);
  const totalsFromStores = sumGiftTotals(storeAllocations);
  const hasStoreAllocations = storeAllocations.length > 0;

  return {
    id: gift.id,
    name: gift.name || '',
    description: gift.description,
    imageUrl: gift.imageUrl,
    isActive: gift.isActive !== false,
    storeAllocations,
    resetConfig: normalizeResetConfig(gift.resetConfig),
    totalQuantity: hasStoreAllocations ? totalsFromStores.totalQuantity : Number(gift.totalQuantity ?? 0),
    availableQuantity: hasStoreAllocations ? totalsFromStores.availableQuantity : Number(gift.availableQuantity ?? gift.totalQuantity ?? 0),
    assignedCount: hasStoreAllocations ? totalsFromStores.assignedCount : Number(gift.assignedCount ?? 0),
    usedQuantity: hasStoreAllocations ? totalsFromStores.usedQuantity : Number(gift.usedQuantity ?? 0),
    assignedUsers: gift.assignedUsers || [],
    redeemedUsers: gift.redeemedUsers || [],
    createdAt: typeof gift.createdAt === 'string' ? gift.createdAt : undefined,
    updatedAt: typeof gift.updatedAt === 'string' ? gift.updatedAt : undefined,
  };
};

const normalizeGiftDoc = (id: string, data: Record<string, any>): Gift => {
  const createdAt = data.createdAt?.toDate?.()?.toISOString?.() || data.createdAt;
  const updatedAt = data.updatedAt?.toDate?.()?.toISOString?.() || data.updatedAt;

  return normalizeGift({
    ...data,
    id,
    createdAt,
    updatedAt,
  });
};

const uploadGiftImage = async (imageFile: File): Promise<string> => {
  const storageRef = ref(storage, `gift-images/${Date.now()}_${imageFile.name}`);
  const uploadResult = await uploadBytes(storageRef, imageFile);
  return getDownloadURL(uploadResult.ref);
};

const removeGiftImageIfNeeded = async (imageUrl?: string) => {
  if (!imageUrl || !isFirebaseStorageUrl(imageUrl)) {
    return;
  }

  try {
    await deleteObject(ref(storage, imageUrl));
  } catch (error) {
    console.error(`Error deleting gift image ${imageUrl}:`, error);
  }
};

const getGiftCollection = () => collection(db, COLLECTION_NAME);

const getGiftDoc = (id: string) => doc(db, COLLECTION_NAME, id);

const getAssignmentCollection = () => collection(db, ASSIGNMENTS_COLLECTION_NAME);

const getAssignmentDoc = (id: string) => doc(db, ASSIGNMENTS_COLLECTION_NAME, id);

const getAssignmentHistoryDoc = (id: string) => doc(db, ASSIGNMENTS_HISTORY_COLLECTION_NAME, id);

const normalizeDateValue = (value: any): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (value instanceof Date) return value.toISOString();
  return value.toDate?.()?.toISOString?.();
};

const normalizeGiftAssignment = (data: Record<string, any>, fallbackId = ''): GiftAssignment => {
  const metadata = data.metadata || {};

  return {
    assignmentId: String(data.assignmentId || data.id || fallbackId),
    giftId: String(data.giftId || ''),
    userId: String(data.userId || data.userInfo?.id || ''),
    storeId: data.storeId || metadata.storeId,
    storeName: data.storeName || metadata.storeName,
    status: data.status || 'assigned',
    assignedAt: normalizeDateValue(data.assignedAt) || normalizeDateValue(data.createdAt) || '',
    redeemedAt: normalizeDateValue(data.redeemedAt),
    qrTarget: data.qrTarget || '',
    qrQuery: data.qrQuery,
    qrCode: data.qrCode,
    userInfo: data.userInfo,
    metadata,
  };
};

const normalizeGiftAssignmentDoc = (id: string, data: Record<string, any>): GiftAssignment => (
  normalizeGiftAssignment(data, id)
);

const fetchGiftAssignmentsByGiftId = async (giftId: string): Promise<GiftAssignment[]> => {
  const querySnapshot = await withTimeout(
    getDocs(query(getAssignmentCollection(), where('giftId', '==', giftId))),
    FIRESTORE_READ_TIMEOUT_MS,
    `Timeout reading gift assignments for ${giftId}`,
  );

  return querySnapshot.docs.map((snapshot) => normalizeGiftAssignmentDoc(snapshot.id, snapshot.data()));
};

const summarizeAssignmentUser = (assignment: GiftAssignment): GiftUserSummary => ({
  assignmentId: assignment.assignmentId,
  userId: assignment.userId,
  name: assignment.userInfo?.name,
  phone: assignment.userInfo?.phone,
  storeId: assignment.storeId || assignment.metadata?.storeId,
  storeName: assignment.storeName || assignment.metadata?.storeName,
  assignedAt: assignment.assignedAt,
  redeemedAt: assignment.redeemedAt,
});

const reconcileGiftCountsFromAssignments = async (gift: Gift): Promise<Gift> => {
  try {
    const assignments = await fetchGiftAssignmentsByGiftId(gift.id);
    if (assignments.length === 0) {
      return gift;
    }

    const allocations = normalizeStoreAllocations(gift.storeAllocations);
    if (allocations.length === 0) {
      return gift;
    }

    const allocationMap = new Map(
      allocations.map((allocation) => [
        allocation.storeId,
        {
          ...allocation,
          assignedCount: 0,
          usedQuantity: 0,
        },
      ]),
    );

    const assignedUsers: GiftUserSummary[] = [];
    const redeemedUsers: GiftUserSummary[] = [];

    assignments.forEach((assignment) => {
      const storeId = assignment.storeId || assignment.metadata?.storeId;
      const resolvedStoreId = storeId && allocationMap.has(storeId)
        ? storeId
        : allocations.find((allocation) => allocation.storeName.trim().toLowerCase() === (assignment.storeName || assignment.metadata?.storeName || '').trim().toLowerCase())?.storeId;
      const allocation = resolvedStoreId ? allocationMap.get(resolvedStoreId) : undefined;

      if (allocation) {
        if (assignment.status === 'redeemed') {
          allocation.usedQuantity += 1;
        } else {
          allocation.assignedCount += 1;
        }
      }

      const summary = summarizeAssignmentUser(assignment);
      if (assignment.status === 'redeemed') {
        redeemedUsers.push(summary);
      } else {
        assignedUsers.push(summary);
      }
    });

    const nextStoreAllocations = Array.from(allocationMap.values()).map((allocation) => ({
      ...allocation,
      availableQuantity: Math.max(0, allocation.totalQuantity - allocation.assignedCount - allocation.usedQuantity),
    }));

    return normalizeGift({
      ...gift,
      storeAllocations: nextStoreAllocations,
      assignedUsers,
      redeemedUsers,
    });
  } catch (error) {
    console.warn(`Could not reconcile gift ${gift.id} counts from assignments:`, error);
    return gift;
  }
};

const normalizeAssignmentQRResponse = (data: Record<string, any>): GiftAssignmentQRResponse => {
  const metadata = data.metadata || {};

  return {
    assignmentId: String(data.assignmentId || data.id || ''),
    status: data.status || 'assigned',
    storeId: data.storeId || metadata.storeId,
    storeName: data.storeName || metadata.storeName,
    qrTarget: data.qrTarget || '',
    qrQuery: data.qrQuery,
    qrCode: data.qrCode || '',
  };
};

const buildGiftPayload = (
  base: {
    name: string;
    description?: string;
    imageUrl?: string;
    isActive?: boolean;
    storeAllocations?: Partial<GiftStoreAllocation>[];
    resetConfig?: Partial<GiftResetConfig>;
    createdAt?: Date;
  },
) => {
  const storeAllocations = normalizeStoreAllocations(base.storeAllocations);
  const totals = sumGiftTotals(storeAllocations);
  const resetConfig = normalizeResetConfig(base.resetConfig);

  return {
    name: base.name,
    description: base.description || '',
    isActive: base.isActive !== false,
    storeAllocations,
    ...(resetConfig ? { resetConfig } : {}),
    totalQuantity: totals.totalQuantity,
    availableQuantity: totals.availableQuantity,
    assignedCount: totals.assignedCount,
    usedQuantity: totals.usedQuantity,
    createdAt: base.createdAt ?? new Date(),
    updatedAt: new Date(),
    ...(base.imageUrl !== undefined ? { imageUrl: base.imageUrl } : {}),
  };
};

const fetchApiGiftsFallback = async (): Promise<Gift[]> => {
  const response = await axios.get(`${API_URL}/gifts`);
  return (response.data as Gift[]).map((gift) => normalizeGift({ ...gift, id: gift.id }));
};

const getDailyResetBoundary = (referenceTime: Date, resetConfig: GiftResetConfig): Date => {
  const boundary = new Date(referenceTime);
  boundary.setHours(resetConfig.dailyResetHour, resetConfig.dailyResetMinute, 0, 0);
  return boundary;
};

const shouldResetGiftNow = (gift: Gift, referenceTime = new Date()): boolean => {
  if (!gift.resetConfig?.enabled) {
    return false;
  }

  const boundary = getDailyResetBoundary(referenceTime, gift.resetConfig);
  if (referenceTime.getTime() < boundary.getTime()) {
    return false;
  }

  if (!gift.resetConfig.lastResetAt) {
    return true;
  }

  const lastResetTime = new Date(gift.resetConfig.lastResetAt).getTime();
  if (Number.isNaN(lastResetTime)) {
    return true;
  }

  return lastResetTime < boundary.getTime();
};

const buildResetGiftData = (gift: Gift, resetAt: Date) => {
  const nextStoreAllocations = normalizeStoreAllocations(gift.storeAllocations).map((allocation) => ({
    ...allocation,
    availableQuantity: allocation.totalQuantity,
    assignedCount: 0,
    usedQuantity: 0,
  }));

  const totals = sumGiftTotals(nextStoreAllocations);

  return {
    storeAllocations: nextStoreAllocations,
    totalQuantity: gift.storeAllocations?.length ? totals.totalQuantity : gift.totalQuantity,
    availableQuantity: gift.storeAllocations?.length ? totals.availableQuantity : gift.totalQuantity,
    assignedCount: 0,
    usedQuantity: 0,
    assignedUsers: [],
    redeemedUsers: [],
    resetConfig: normalizeResetConfig({
      ...gift.resetConfig,
      lastResetAt: resetAt.toISOString(),
    }),
    updatedAt: resetAt,
  };
};

const archiveAndClearGiftAssignments = async (giftId: string, resetAt: Date): Promise<void> => {
  if (!giftId) return;

  const querySnapshot = await withTimeout(
    getDocs(query(getAssignmentCollection(), where('giftId', '==', giftId))),
    FIRESTORE_READ_TIMEOUT_MS,
    `Timeout reading gift assignments for ${giftId}`,
  );

  if (querySnapshot.empty) {
    return;
  }

  const archivedAtIso = resetAt.toISOString();
  const docs = querySnapshot.docs;

  for (let index = 0; index < docs.length; index += FIRESTORE_BATCH_LIMIT) {
    const chunk = docs.slice(index, index + FIRESTORE_BATCH_LIMIT);
    const batch = writeBatch(db);

    chunk.forEach((snapshot) => {
      const data = snapshot.data() || {};
      const status = data.status || 'assigned';

      // Chỉ giữ lịch sử các lượt đã redeemed; lượt assigned chưa dùng coi như mất hiệu lực sau reset.
      if (status === 'redeemed') {
        batch.set(getAssignmentHistoryDoc(snapshot.id), {
          ...data,
          giftId,
          originalAssignmentId: snapshot.id,
          archivedAt: resetAt,
          archivedAtIso,
          archivedReason: 'daily-reset',
        });
      }

      batch.delete(snapshot.ref);
    });

    await batch.commit();
  }
};

const maybeResetGiftDocument = async (gift: Gift): Promise<Gift> => {
  if (!shouldResetGiftNow(gift)) {
    return gift;
  }

  const resetAt = new Date();
  const resetData = buildResetGiftData(gift, resetAt);

  await archiveAndClearGiftAssignments(gift.id, resetAt);
  await updateDoc(getGiftDoc(gift.id), resetData);

  return normalizeGift({
    ...gift,
    ...resetData,
    updatedAt: resetAt.toISOString(),
  });
};

const resolveAssignmentStoreId = async (
  gift: Gift,
  assignmentId: string,
  assignedUser?: GiftUserSummary,
  redeemedUser?: GiftUserSummary,
): Promise<string | undefined> => {
  const directStoreId = assignedUser?.storeId || redeemedUser?.storeId;
  if (directStoreId) {
    return directStoreId;
  }

  const normalizedStoreName = assignedUser?.storeName?.trim() || redeemedUser?.storeName?.trim();
  if (normalizedStoreName) {
    const matchedByName = gift.storeAllocations?.find(
      (allocation) => allocation.storeName.trim().toLowerCase() === normalizedStoreName.toLowerCase(),
    );
    if (matchedByName?.storeId) {
      return matchedByName.storeId;
    }
  }

  try {
    const response = await axios.get(`${API_URL}/gifts/assignment/${assignmentId}`);
    const assignmentData = response.data as {
      storeId?: string;
      metadata?: { storeId?: string };
      assignment?: { storeId?: string; metadata?: { storeId?: string } };
    };

    const apiStoreId =
      assignmentData.storeId
      || assignmentData.metadata?.storeId
      || assignmentData.assignment?.storeId
      || assignmentData.assignment?.metadata?.storeId;

    if (apiStoreId) {
      return apiStoreId;
    }
  } catch (error) {
    console.warn(`Could not resolve storeId for assignment ${assignmentId} from API:`, error);
  }

  const allocations = normalizeStoreAllocations(gift.storeAllocations);
  if (allocations.length === 1) {
    return allocations[0].storeId;
  }

  const positiveAssignedCandidates = allocations.filter((allocation) => allocation.assignedCount > 0);
  if (positiveAssignedCandidates.length === 1) {
    return positiveAssignedCandidates[0].storeId;
  }

  const positiveUsedCandidates = allocations.filter((allocation) => allocation.usedQuantity > 0);
  if (positiveUsedCandidates.length === 1) {
    return positiveUsedCandidates[0].storeId;
  }

  return undefined;
};

const ensureGiftDocExists = async (id: string): Promise<Record<string, any>> => {
  const docRef = getGiftDoc(id);
  const currentDoc = await withTimeout(
    getDoc(docRef),
    FIRESTORE_READ_TIMEOUT_MS,
    `Timeout ensuring gift ${id} in Firestore`,
  );

  if (currentDoc.exists()) {
    return currentDoc.data();
  }

  const fallbackGift = await giftService.getGiftDetail(id);
  const fallbackData = buildGiftPayload({
    name: fallbackGift.name,
    description: fallbackGift.description,
    imageUrl: fallbackGift.imageUrl,
    isActive: fallbackGift.isActive,
    storeAllocations: fallbackGift.storeAllocations,
    resetConfig: fallbackGift.resetConfig,
    createdAt: fallbackGift.createdAt ? new Date(fallbackGift.createdAt) : new Date(),
  });

  await setDoc(docRef, {
    ...fallbackData,
    assignedUsers: fallbackGift.assignedUsers || [],
    redeemedUsers: fallbackGift.redeemedUsers || [],
  }, { merge: true });

  return {
    ...fallbackData,
    assignedUsers: fallbackGift.assignedUsers || [],
    redeemedUsers: fallbackGift.redeemedUsers || [],
  };
};

export const giftService = {
  async getGifts(): Promise<Gift[]> {
    try {
      const querySnapshot = await withTimeout(
        getDocs(getGiftCollection()),
        FIRESTORE_READ_TIMEOUT_MS,
        'Timeout reading gifts from Firestore',
      );
      const gifts = querySnapshot.docs.map((snapshot) => normalizeGiftDoc(snapshot.id, snapshot.data()));

      if (gifts.length > 0) {
        return Promise.all(gifts.map(async (gift) => reconcileGiftCountsFromAssignments(await maybeResetGiftDocument(gift))));
      }
    } catch (error) {
      console.error('Error fetching gifts from Firestore:', error);
    }

    try {
      return await fetchApiGiftsFallback();
    } catch (error) {
      throw new Error('Could not fetch gifts: ' + error);
    }
  },

  async getGiftDetail(id: string): Promise<Gift> {
    try {
      const docSnap = await withTimeout(
        getDoc(getGiftDoc(id)),
        FIRESTORE_READ_TIMEOUT_MS,
        `Timeout reading gift ${id} from Firestore`,
      );
      if (docSnap.exists()) {
        return reconcileGiftCountsFromAssignments(await maybeResetGiftDocument(normalizeGiftDoc(docSnap.id, docSnap.data())));
      }
    } catch (error) {
      console.error('Error fetching gift detail from Firestore:', error);
    }

    try {
      const response = await axios.get(`${API_URL}/gifts/gift/${id}`);
      return normalizeGift({ ...(response.data as Gift), id });
    } catch (error) {
      throw new Error('Could not fetch gift detail: ' + error);
    }
  },

  async createGift(payload: CreateGiftPayload, imageFile?: File): Promise<Gift> {
    try {
      const imageUrl = imageFile ? await uploadGiftImage(imageFile) : undefined;
      const now = new Date();
      const giftData = buildGiftPayload({
        name: payload.name,
        description: payload.description,
        imageUrl,
        isActive: payload.isActive,
        storeAllocations: payload.storeAllocations,
        resetConfig: payload.resetConfig,
        createdAt: now,
      });

      const docRef = await addDoc(getGiftCollection(), {
        ...giftData,
        assignedUsers: [],
        redeemedUsers: [],
      });

      return normalizeGiftDoc(docRef.id, {
        ...giftData,
        assignedUsers: [],
        redeemedUsers: [],
      });
    } catch (error) {
      throw new Error('Could not create gift: ' + error);
    }
  },

  async updateGift(id: string, payload: UpdateGiftPayload, imageFile?: File): Promise<{ message: string }> {
    try {
      const docRef = getGiftDoc(id);
      const currentData = await ensureGiftDocExists(id);
      const normalizedCurrentGift = normalizeGiftDoc(id, currentData);

      let imageUrl = currentData.imageUrl;
      if (imageFile) {
        await removeGiftImageIfNeeded(currentData.imageUrl);
        imageUrl = await uploadGiftImage(imageFile);
      }

      const updateData = buildGiftPayload({
        name: payload.name ?? normalizedCurrentGift.name,
        description: payload.description ?? normalizedCurrentGift.description,
        imageUrl,
        isActive: payload.isActive ?? normalizedCurrentGift.isActive,
        storeAllocations: payload.storeAllocations ?? normalizedCurrentGift.storeAllocations,
        resetConfig: payload.resetConfig ?? normalizedCurrentGift.resetConfig,
        createdAt: normalizedCurrentGift.createdAt ? new Date(normalizedCurrentGift.createdAt) : new Date(),
      });

      await updateDoc(docRef, updateData);
      return { message: 'Gift updated successfully' };
    } catch (error) {
      throw new Error('Could not update gift: ' + error);
    }
  },

  async deleteGift(id: string): Promise<{ message: string }> {
    try {
      const docRef = getGiftDoc(id);
      const currentDoc = await getDoc(docRef);

      if (currentDoc.exists()) {
        await removeGiftImageIfNeeded(currentDoc.data().imageUrl);
      }

      await deleteDoc(docRef);
      return { message: 'Gift deleted successfully' };
    } catch (error) {
      throw new Error('Could not delete gift: ' + error);
    }
  },

  async toggleGiftStatus(id: string, isActive: boolean): Promise<{ message: string }> {
    try {
      await ensureGiftDocExists(id);
      await updateDoc(getGiftDoc(id), {
        isActive,
        updatedAt: new Date(),
      });
      return { message: 'Gift status updated successfully' };
    } catch (error) {
      throw new Error('Could not toggle gift status: ' + error);
    }
  },

  async resetGiftNow(id: string): Promise<ManualResetGiftResponse> {
    try {
      const response = await axios.post<ManualResetGiftResponse>(`${API_URL}/gifts/${id}/reset`);
      return response.data;
    } catch (error) {
      throw new Error('Could not reset gift: ' + error);
    }
  },

  async resetAllGifts(): Promise<ManualResetAllResponse> {
    try {
      const response = await axios.post<ManualResetAllResponse>(`${API_URL}/gifts/reset-all`);
      return response.data;
    } catch (error) {
      throw new Error('Could not reset all gifts: ' + error);
    }
  },

  async assignGift(payload: AssignGiftPayload): Promise<GiftAssignment> {
    try {
      const currentGift = await this.getGiftDetail(payload.giftId);
      const currentAllocation = currentGift.storeAllocations?.find((allocation) => allocation.storeId === payload.storeId);

      if (!currentAllocation || currentAllocation.totalQuantity <= 0) {
        throw new Error('Quà chưa được phân bổ cho cơ sở này');
      }

      if (currentAllocation.availableQuantity <= 0) {
        throw new Error('Cơ sở này đã hết quà');
      }

      const response = await axios.post(`${API_URL}/gifts/assign`, payload);
      return normalizeGiftAssignment(response.data as Record<string, any>);
    } catch (error) {
      throw new Error('Could not assign gift: ' + error);
    }
  },

  async getAssignmentQR(assignmentId: string): Promise<GiftAssignmentQRResponse> {
    try {
      const response = await axios.get(`${API_URL}/gifts/qr/${assignmentId}`);
      return normalizeAssignmentQRResponse(response.data as Record<string, any>);
    } catch (error) {
      throw new Error('Could not fetch assignment QR: ' + error);
    }
  },

  async getGiftAssignments(giftId: string): Promise<GiftAssignment[]> {
    try {
      return await fetchGiftAssignmentsByGiftId(giftId);
    } catch (error) {
      throw new Error('Could not fetch gift assignments: ' + error);
    }
  },

  async deleteGiftAssignment(giftId: string, assignmentId: string): Promise<{ message: string }> {
    try {
      await ensureGiftDocExists(giftId);

      await runTransaction(db, async (transaction) => {
        const docRef = getGiftDoc(giftId);
        const docSnap = await transaction.get(docRef);

        if (!docSnap.exists()) {
          throw new Error('Không tìm thấy quà tặng trong Firebase');
        }

        const gift = normalizeGiftDoc(docSnap.id, docSnap.data());
        const assignedUsers = gift.assignedUsers || [];
        const redeemedUsers = gift.redeemedUsers || [];
        const assignedUser = assignedUsers.find((user) => user.assignmentId === assignmentId);
        const redeemedUser = redeemedUsers.find((user) => user.assignmentId === assignmentId);
        const assignmentRef = getAssignmentDoc(assignmentId);
        const assignmentSnap = await transaction.get(assignmentRef);
        const assignmentData = assignmentSnap.exists()
          ? normalizeGiftAssignmentDoc(assignmentSnap.id, assignmentSnap.data())
          : undefined;

        if (!assignedUser && !assignmentData) {
          throw new Error('Không tìm thấy lượt gán quà');
        }

        const assignmentStoreId = assignmentData?.metadata?.storeId || (assignmentData as any)?.storeId;
        const storeId = assignmentStoreId || await resolveAssignmentStoreId(gift, assignmentId, assignedUser, redeemedUser);
        const shouldDecreaseUsed = Boolean(redeemedUser || assignmentData?.status === 'redeemed');
        const nextAllocations = normalizeStoreAllocations(gift.storeAllocations).map((allocation) => {
          if (!storeId || allocation.storeId !== storeId) {
            return allocation;
          }

          return {
            ...allocation,
            availableQuantity: Math.min(allocation.totalQuantity, allocation.availableQuantity + 1),
            assignedCount: Math.max(0, allocation.assignedCount - 1),
            usedQuantity: shouldDecreaseUsed ? Math.max(0, allocation.usedQuantity - 1) : allocation.usedQuantity,
          };
        });

        const totals = sumGiftTotals(nextAllocations);

        transaction.update(docRef, {
          storeAllocations: nextAllocations,
          totalQuantity: nextAllocations.length > 0 ? totals.totalQuantity : Math.max(0, (gift.totalQuantity || 0)),
          availableQuantity: nextAllocations.length > 0 ? totals.availableQuantity : Math.max(0, (gift.availableQuantity || 0) + 1),
          assignedCount: nextAllocations.length > 0 ? totals.assignedCount : Math.max(0, (gift.assignedCount || 0) - 1),
          usedQuantity: nextAllocations.length > 0
            ? totals.usedQuantity
            : (redeemedUser ? Math.max(0, (gift.usedQuantity || 0) - 1) : Math.max(0, gift.usedQuantity || 0)),
          assignedUsers: assignedUsers.filter((user) => user.assignmentId !== assignmentId),
          redeemedUsers: redeemedUsers.filter((user) => user.assignmentId !== assignmentId),
          updatedAt: new Date(),
        });

        if (assignmentSnap.exists()) {
          transaction.delete(assignmentRef);
        }
      });

      return { message: 'Gift assignment deleted successfully' };
    } catch (error) {
      throw new Error('Could not delete gift assignment: ' + error);
    }
  },

  async redeemGift(payload: RedeemGiftPayload): Promise<RedeemGiftResponse> {
    try {
      const response = await axios.post(`${API_URL}/gifts/redeem`, payload);
      const redeemResponse = response.data as RedeemGiftResponse;
      return {
        ...redeemResponse,
        assignment: normalizeGiftAssignment(redeemResponse.assignment as unknown as Record<string, any>),
      };
    } catch (error) {
      throw new Error('Could not redeem gift: ' + error);
    }
  },
};
